<template>
  <div
    class="article-hero"
    :class="classes"
    :style="{
      '--backgroundImageSmall': `url(${images?.smallMedia})`,
      '--backgroundImageMedium': `url(${images?.mediumMedia})`,
      '--backgroundImageLarge': `url(${images?.largeMedia})`,
    }"
  >
    <template v-if="videoUrl && !lowPower">
      <Media
        :sources="[
          {
            url: videoUrl,
          },
        ]"
        cover
        class="media"
        @lowPower="lowPower = true"
      />
    </template>
    <div class="copy" :class="copyClasses">
      <div v-if="type" class="type">{{ type }}</div>
      <h1 class="heading">
        <template v-if="heading">{{ heading }}</template>
        <template v-else><slot name="heading" /></template>
      </h1>
      <div v-if="subheading" class="subheading">{{ subheading }}</div>
      <div class="button-container">
        <Button
          v-if="linkButton"
          theme="white"
          class="linkButton"
          tag="a"
          :href="link"
        >
          {{ linkButton }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
import Button from "@/components/Button.vue";
import Media from "@/components/Media.vue";
export default {
  name: "ArticleHero",
  components: {
    Button,
    Media,
  },
  props: {
    heading: {
      type: String,
      default: null,
    },
    subheading: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
    images: {
      type: Object,
      default: null,
    },
    heroBackgroundPostitionTop: {
      type: Boolean,
      default: false,
    },
    centerTextWithSpanItalic: {
      type: Boolean,
      default: false,
    },
    linkButton: {
      type: String,
      default: null,
    },
    link: {
      type: String,
      default: null,
    },
    textLeftAlign: {
      type: Boolean,
      default: false,
    },
    styleSeries: {
      type: Boolean,
      default: false,
    },
    videoMobile: {
      type: Boolean,
      default: false,
    },
    videoUrl: {
      type: String,
      default: null,
    },
    lowPowerImages: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      lowPower: false,
    };
  },
  computed: {
    backgroundImages() {
      if (this.lowPower) {
        return this.lowPowerImages;
      }

      return this.images;
    },
    classes() {
      return {
        "article-hero--heroBackgroundPostitionTop":
          this.heroBackgroundPostitionTop,
        "article-hero--videoMobile": this.videoMobile,
        "copy--center-text-with--span-italic": this.centerTextWithSpanItalic,
        "copy--text-left-align": this.textLeftAlign,
      };
    },
    copyClasses() {
      return {
        "copy--center-text-with--span-italic": this.centerTextWithSpanItalic,
        "copy--text-left-align": this.textLeftAlign,
        "copy--style-series": this.styleSeries,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.article-hero {
  position: relative;
  height: 70vh;
  overflow: hidden;
  background-image: var(--backgroundImageSmall);
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center center;

  @include respond-above(sm) {
    background-image: var(--backgroundImageMedium);
  }

  @include respond-above(md) {
    height: 85vh;
    background-image: var(--backgroundImageLarge);
  }

  &::after {
    content: "";
    background: linear-gradient(
      180deg,
      rgba(89, 89, 89, 0) 0%,
      rgba(1, 1, 1, 0.6) 100%
    );
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 75%;
  }

  &--heroBackgroundPostitionTop {
    background-position: top center;
  }
  &--videoMobile {
    height: auto;
  }
}
.type {
  color: $color-white;
  @include t__editorial-headline-10;

  @include respond-above(md) {
    @include t__editorial-headline-20;
  }
}

.subheading {
  color: $color-white;
  @include t__editorial-headline-10;

  @include respond-above(md) {
    @include t__editorial-headline-20;
  }
}

.copy {
  width: 100%;
  position: absolute;
  text-align: center;
  z-index: 1;
  top: auto;
  bottom: 60px;
  padding: 0 20px;
  text-wrap: balance;

  @include respond-above(md) {
    bottom: 40px;
    padding: 0;
  }
  &--center-text-with--span-italic {
    top: 50%;
    .heading {
      @include t__editorial-headline-50;
      @include respond-above(md) {
        @include t__editorial-headline-80;
      }
      :slotted(p) {
        @include respond-above(md) {
          display: inline;
        }
      }
      :slotted(span) {
        text-wrap: pretty;
        display: block;
        font-style: italic;
        @include respond-above(md) {
          display: inline;
        }
      }
    }
    .subheading {
      text-transform: none;
      font-weight: 400;
    }
  }
  &--text-left-align {
    @include respond-above(md) {
      text-align: left;
    }
    .heading {
      font-size: 50px;
      @include respond-above(md) {
        @include t__editorial-headline-80;
      }
    }
    .subheading {
      text-transform: none;
      @include respond-above(md) {
        max-width: 1100px;
        margin: 0 auto;
      }
    }
    .button-container {
      margin: 20px auto;
      @include respond-above(md) {
        max-width: 1100px;
      }
    }
  }
  &--style-series {
    bottom: 60px;
    .heading {
      font-size: 36px;
      margin: 0 auto 16px;
      line-height: 100%;
      @include respond-above(md) {
        bottom: 80px;
        @include t__editorial-headline-80;
      }
    }
    .subheading {
      text-transform: none;
      font-size: 16px;
      font-weight: 500;
    }
  }
}

.heading {
  color: rgb(255, 255, 255);
  @include t__editorial-headline-30;

  text-transform: uppercase;

  @include respond-above(md) {
    margin: 0 auto;
    max-width: 1100px;
    @include t__editorial-headline-50;
  }
}

:deep(.picture__wrapper) {
  display: block;
}

.heading :slotted(span) {
  display: block;
}

.linkButton {
  background-color: $color-white;
  color: $color-grey-dark;
  font-family: $utility-font-family;
  margin-top: 20px;
  font-weight: 400;
  font-size: 11px;
  padding: 14px 28px;
  border-radius: 4px;
  @include respond-above(md) {
    font-size: 13px;
  }
}

.media {
  height: 100%;
}
</style>
